import React from 'react';
import {
  Modal,
  Typography,
  Form,
  Input,
  Select,
  Switch,
  Button,
  useToast,
} from '@crisellevaldez/speculo-ui';
import { BellRing, Home, LayoutGrid, MapPin, Info } from 'lucide-react';
import { IconPill } from '../../../common/IconPill';
import { useForm, Controller, Control, FieldPath } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { bulkEAlertSchema, BulkEAlertFormData } from '../../../../schemas/bulkEAlert.schema';
import { Organization } from '../../../../types/organization.types';
import {
  useRegisterBulkEAlert,
  useBulkUpdateEAlertFilters,
  type BulkUpdateEAlertFiltersData,
  type BulkEAlertData,
} from '../../../../hooks/client/email/useBulkEAlerts';
import { useEmailContactTags } from '../../../../hooks/email/useEmailContactTags';
import { US_STATES } from '../../../../constants/states';
import { useUSCanadaCities } from '../../../../hooks/cities/useUSCanadaCities';
import { CreatableSelect } from '../../../common/CreatableSelect';

// Feature toggle component for consistent styling
const FeatureToggle: React.FC<{
  label: string;
  name: FieldPath<BulkEAlertFormData>;
  control: Control<BulkEAlertFormData>;
}> = ({ label, name, control }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Switch
          checked={Boolean(field.value)}
          onChange={(checked) => field.onChange(checked)}
          size="sm"
        />
      )}
    />
  </div>
);

interface EmailCampaignEAlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  organization?: Organization;
  initialData?: {
    tag: string;
  };
  isUpdateMode?: boolean;
}

export const EmailCampaignEAlertModal: React.FC<EmailCampaignEAlertModalProps> = ({
  isOpen,
  onClose,
  organization,
  initialData,
  isUpdateMode = false,
}) => {
  const { showToast } = useToast();
  useEmailContactTags(organization?.id, isOpen);

  // Use the custom hooks for eAlert operations
  const { mutate: registerBulkEAlert, isPending: isSubmitting } = useRegisterBulkEAlert();
  const { mutate: bulkUpdateEAlertFilters, isPending: isUpdating } = useBulkUpdateEAlertFilters();

  const stateOptions = US_STATES;

  // Initialize form with react-hook-form and zod validation
  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<BulkEAlertFormData>({
    resolver: zodResolver(bulkEAlertSchema),
    defaultValues: {
      organization_id: organization?.id || '',
      tag: initialData?.tag || '',
      interval: 'weekly',
      city: '',
      state: '',
      subdivision: '',
      q: '',
      bedrooms: undefined,
      bathrooms: undefined,
      min_price: undefined,
      max_price: undefined,
      property_type: 'Single Family Residence',
      propertysubtype: '',
      status: 'Active',
      waterfront: false,
      pool: false,
      basement: false,
      garage: false,
      city_view: false,
      mountain_view: false,
      park_view: false,
      water_view: false,
      sqft_min: undefined,
      sqft_max: undefined,
    },
  });

  // Selected state drives the city-picker suggestion list.
  const selectedState = watch('state');
  const { cities: cityOptions, isLoading: isCitiesLoading } = useUSCanadaCities(selectedState);

  // Filter out false boolean values from form data
  const filterFalseValues = (data: BulkEAlertFormData): BulkEAlertFormData => {
    const filteredData = { ...data };

    // List of boolean fields that should be omitted if false
    const booleanFields = [
      'waterfront',
      'pool',
      'basement',
      'garage',
      'city_view',
      'mountain_view',
      'park_view',
      'water_view',
    ] as const;

    // Remove fields with false values
    booleanFields.forEach((field) => {
      if (filteredData[field] === false) {
        delete filteredData[field];
      }
    });

    return filteredData as BulkEAlertFormData;
  };

  // Handle form submission for registration
  const onSubmit = (data: BulkEAlertFormData) => {
    console.log('Form submitted with data:', data);
    // Ensure organization_id is set
    if (organization?.id) {
      data.organization_id = organization.id;
    }

    // Ensure tag is set from initialData if it exists
    if (initialData?.tag && (!data.tag || data.tag === '')) {
      data.tag = initialData.tag;
    }

    // Convert any string values to proper types
    const formattedData = {
      ...data,
      bedrooms:
        typeof data.bedrooms === 'string'
          ? data.bedrooms === ''
            ? undefined
            : parseInt(data.bedrooms, 10)
          : data.bedrooms,
      bathrooms:
        typeof data.bathrooms === 'string'
          ? data.bathrooms === ''
            ? undefined
            : parseFloat(data.bathrooms)
          : data.bathrooms,
      min_price:
        typeof data.min_price === 'string'
          ? data.min_price === ''
            ? undefined
            : parseInt(data.min_price, 10)
          : data.min_price,
      max_price:
        typeof data.max_price === 'string'
          ? data.max_price === ''
            ? undefined
            : parseInt(data.max_price, 10)
          : data.max_price,
      sqft_min:
        typeof data.sqft_min === 'string'
          ? data.sqft_min === ''
            ? undefined
            : parseInt(data.sqft_min, 10)
          : data.sqft_min,
      sqft_max:
        typeof data.sqft_max === 'string'
          ? data.sqft_max === ''
            ? undefined
            : parseInt(data.sqft_max, 10)
          : data.sqft_max,
    };

    // Filter out false boolean values from the formatted data
    const filteredData = filterFalseValues(formattedData);

    // Set default min_price to 50000 if not provided
    if (!filteredData.min_price) {
      filteredData.min_price = 50000;
    }

    if (isUpdateMode) {
      const updatePayload = { bulk: true, ...filteredData } as BulkUpdateEAlertFiltersData;
      bulkUpdateEAlertFilters(updatePayload, {
        onSuccess: () => {
          showToast({
            message: 'Bulk eAlert filters updated successfully',
            variant: 'success',
          });
          reset();
          onClose();
        },
        onError: (error) => {
          const message =
            error instanceof Error && error.message
              ? error.message
              : 'Failed to update bulk eAlert filters';
          showToast({ message, variant: 'error' });
        },
      });
    } else {
      const registerPayload = filteredData as BulkEAlertData;
      registerBulkEAlert(registerPayload, {
          onSuccess: () => {
            showToast({
              message: 'Bulk eAlert registered successfully',
              variant: 'success',
            });
            reset();
            onClose();
          },
          onError: (error) => {
            const message =
              error instanceof Error && error.message
                ? error.message
                : 'Failed to register bulk eAlert';
            showToast({ message, variant: 'error' });
          },
        }
      );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <Modal.Header>
        <div className="flex items-center justify-between w-full">
          <Typography variant="h3">
            {isUpdateMode ? 'Update Property eAlert Filters' : 'Set Up Property eAlert'}
          </Typography>
        </div>
      </Modal.Header>
      <Modal.Body className="py-8">
        <div className="space-y-6">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700/30 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-yellow-800 dark:text-yellow-400" />
              <p className="text-sm text-yellow-800 dark:text-yellow-300">
                Set up advanced property alerts for contacts with a specific tag. They will receive
                notifications based on these preferences.
              </p>
            </div>
          </div>
          <form
            id="bulk-ealert-form"
            onSubmit={(e) => {
              if (initialData?.tag) {
                setValue('tag', initialData.tag);
              }

              handleSubmit((formData) => {
                console.log('Form validation passed, calling onSubmit');
                onSubmit(formData);
              })(e);
            }}
            className="space-y-6"
          >
            <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15">
              <div className="flex items-center gap-4 mb-3">
                <IconPill icon={BellRing} size="lg" />
                <h4 className="text-sm font-bold text-foreground">Alert Information</h4>
              </div>
              <hr className="dark:border-white/10" />
              <div className="mt-5 space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/30 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2">
                    <Info className="h-4 w-4 text-blue-800 dark:text-blue-400" />
                    <div className="flex flex-col gap-2">
                      <p className="text-sm text-blue-800 dark:text-blue-300">
                        Using tag: <strong>{initialData?.tag || 'None'}</strong>{' '}
                      </p>
                    </div>
                  </div>
                </div>

                <Form.Control className="mt-4">
                  <Form.Label required>Alert Frequency</Form.Label>
                  <Controller
                    name="interval"
                    control={control}
                    render={({ field }) => (
                      <Select
                        placeholder="Select frequency"
                        options={[
                          { value: 'daily', label: 'Daily' },
                          { value: 'weekly', label: 'Weekly' },
                          { value: 'bi-weekly', label: 'Bi-Weekly' },
                        ]}
                        {...field}
                        error={errors.interval?.message}
                      />
                    )}
                  />
                </Form.Control>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15">
              <div className="flex items-center gap-4 mb-3">
                <IconPill icon={MapPin} size="lg" />
                <h4 className="text-sm font-bold text-foreground">Location</h4>
              </div>
              <hr className="dark:border-white/10" />
              <div className="mt-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <Form.Control>
                    <Form.Label>State</Form.Label>
                    <Controller
                      name="state"
                      control={control}
                      render={({ field }) => (
                        <Select
                          placeholder="Select state"
                          options={stateOptions}
                          searchable={true}
                          {...field}
                          onChange={(val) => {
                            field.onChange(val);
                            // Clear the city so a stale "Austin" doesn't ride
                            // through after switching from TX to FL.
                            setValue('city', '');
                          }}
                          error={errors.state?.message}
                        />
                      )}
                    />
                  </Form.Control>
                  <Form.Control>
                    <Form.Label>City</Form.Label>
                    <Controller
                      name="city"
                      control={control}
                      render={({ field }) => (
                        <CreatableSelect
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          options={cityOptions}
                          placeholder={
                            selectedState
                              ? isCitiesLoading
                                ? 'Loading cities…'
                                : 'Type or pick a city'
                              : 'Select a state first'
                          }
                          isLoading={isCitiesLoading}
                          disabled={!selectedState}
                          error={errors.city?.message}
                        />
                      )}
                    />
                  </Form.Control>
                  <Form.Control>
                    <Form.Label>Subdivision</Form.Label>
                    <Input
                      {...register('subdivision')}
                      placeholder="Enter subdivision"
                      error={errors.subdivision?.message}
                    />
                  </Form.Control>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15">
              <div className="flex items-center gap-4 mb-3">
                <IconPill icon={Home} size="lg" />
                <h4 className="text-sm font-bold text-foreground">Search Parameters</h4>
              </div>
              <hr className="dark:border-white/10" />
              <div className="mt-5 space-y-4">
                <Form.Control className="mb-4">
                  <Form.Label>Search Query</Form.Label>
                  <Input
                    {...register('q')}
                    placeholder="e.g., modern homes, waterfront property"
                    error={errors.q?.message}
                  />
                </Form.Control>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15">
              <div className="flex items-center gap-4 mb-3">
                <IconPill icon={Home} size="lg" />
                <h4 className="text-sm font-bold text-foreground">Property Details</h4>
              </div>
              <hr className="dark:border-white/10" />
              <div className="mt-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <Form.Control>
                    <Form.Label>Bedrooms</Form.Label>
                    <Controller
                      name="bedrooms"
                      control={control}
                      render={({ field: { onChange, value, ...rest } }) => (
                        <Select
                          placeholder="Select bedrooms"
                          options={[
                            { value: '', label: 'Any' },
                            { value: '1', label: '1 Bedroom' },
                            { value: '2', label: '2 Bedrooms' },
                            { value: '3', label: '3 Bedrooms' },
                            { value: '4', label: '4 Bedrooms' },
                            { value: '5', label: '5 Bedrooms' },
                          ]}
                          value={value?.toString() || ''}
                          onChange={(val: string | string[]) => {
                            const singleVal = Array.isArray(val) ? val[0] : val;
                            onChange(singleVal === '' ? undefined : parseInt(singleVal, 10));
                          }}
                          {...rest}
                        />
                      )}
                    />
                  </Form.Control>
                  <Form.Control>
                    <Form.Label>Bathrooms</Form.Label>
                    <Controller
                      name="bathrooms"
                      control={control}
                      render={({ field: { onChange, value, ...rest } }) => (
                        <Select
                          placeholder="Select bathrooms"
                          options={[
                            { value: '', label: 'Any' },
                            { value: '1', label: '1 Bathroom' },
                            { value: '2', label: '2 Bathrooms' },
                            { value: '3', label: '3 Bathrooms' },
                            { value: '4', label: '4 Bathrooms' },
                            { value: '5', label: '5 Bathrooms' },
                          ]}
                          value={value?.toString() || ''}
                          onChange={(val: string | string[]) => {
                            const singleVal = Array.isArray(val) ? val[0] : val;
                            onChange(singleVal === '' ? undefined : parseFloat(singleVal));
                          }}
                          {...rest}
                        />
                      )}
                    />
                  </Form.Control>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <Form.Control>
                    <Form.Label>Minimum Price</Form.Label>
                    <Input
                      {...register('min_price')}
                      type="text"
                      inputMode="numeric"
                      placeholder="Enter minimum price"
                      error={errors.min_price?.message}
                    />
                  </Form.Control>
                  <Form.Control>
                    <Form.Label>Maximum Price</Form.Label>
                    <Input
                      {...register('max_price')}
                      type="text"
                      inputMode="numeric"
                      placeholder="Enter maximum price"
                      error={errors.max_price?.message}
                    />
                  </Form.Control>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <Form.Control>
                    <Form.Label>Property Type</Form.Label>
                    <Controller
                      name="property_type"
                      control={control}
                      render={({ field }) => (
                        <Select
                          placeholder="Select property type"
                          options={[
                            { value: '', label: 'Any' },
                            { value: 'Single Family Residence', label: 'Single Family Residence' },
                            { value: 'Condominium', label: 'Condominium' },
                            { value: 'Townhouse', label: 'Townhouse' },
                            { value: 'Land', label: 'Land' },
                          ]}
                          {...field}
                        />
                      )}
                    />
                  </Form.Control>
                  <Form.Control>
                    <Form.Label>Status</Form.Label>
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <Select
                          placeholder="Select status"
                          options={[
                            { value: '', label: 'Any' },
                            { value: 'Active', label: 'Active' },
                            { value: 'Pending', label: 'Pending' },
                            { value: 'Closed', label: 'Closed' },
                          ]}
                          {...field}
                        />
                      )}
                    />
                  </Form.Control>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <Form.Control>
                    <Form.Label>Minimum Square Footage</Form.Label>
                    <Input
                      {...register('sqft_min')}
                      type="text"
                      inputMode="numeric"
                      placeholder="Enter minimum sqft"
                      error={errors.sqft_min?.message}
                    />
                  </Form.Control>
                  <Form.Control>
                    <Form.Label>Maximum Square Footage</Form.Label>
                    <Input
                      {...register('sqft_max')}
                      type="text"
                      inputMode="numeric"
                      placeholder="Enter maximum sqft"
                      error={errors.sqft_max?.message}
                    />
                  </Form.Control>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15">
              <div className="flex items-center gap-4 mb-3">
                <IconPill icon={LayoutGrid} size="lg" />
                <h4 className="text-sm font-bold text-foreground">Features</h4>
              </div>
              <hr className="dark:border-white/10" />
              <div className="mt-5 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FeatureToggle label="Waterfront" name="waterfront" control={control} />
                  <FeatureToggle label="Pool" name="pool" control={control} />
                  <FeatureToggle label="Basement" name="basement" control={control} />
                  <FeatureToggle label="Garage" name="garage" control={control} />
                  <FeatureToggle label="City View" name="city_view" control={control} />
                  <FeatureToggle label="Mountain View" name="mountain_view" control={control} />
                  <FeatureToggle label="Park View" name="park_view" control={control} />
                  <FeatureToggle label="Water View" name="water_view" control={control} />
                </div>
              </div>
            </div>
          </form>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4 w-full">
          <div className="flex gap-2 w-full md:w-auto ml-auto justify-end">
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="bulk-ealert-form"
              isLoading={isSubmitting || isUpdating}
            >
              {isUpdateMode ? 'Update Filters' : 'Register Bulk eAlert'}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
